import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Paper,
  Divider as MuiDivider,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import styled from "styled-components";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import EditModal from "./EditModal";
import ProductNode from "./nodes/ProductNode";
import MaterialNode from "./nodes/MaterialNode";
import SemiProductNode from "./nodes/SemiProductNode";
import TPC_URL from "../../../utils/TPC_URL";
import { useParams } from "react-router-dom";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  background-color: var(--mui-palette-background-default);
  box-sizing: border-box;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
  background-color: var(--mui-palette-background-paper);
  border-radius: 8px 8px 0 0;
  margin-bottom: 0;
  flex-shrink: 0;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const SidePanel = styled(Paper)`
  width: 300px;
  flex-shrink: 0;
  overflow-y: auto;
  border-left: 1px solid var(--mui-palette-divider);
  border-radius: 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  position: relative;
  background-color: var(--mui-palette-background-paper);
  border-radius: 0 0 0 8px;
  box-shadow: var(--mui-shadows-2);
`;

const Title = styled(Typography)`
  color: var(--mui-palette-text-primary);
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  max-width: 500px;
  margin: 0 24px;
`;

// Material_grouping 기준으로 완제품 묶기
const groupItemsByMaterialGrouping = (items) => {
  const map = new Map();

  items.forEach((item) => {
    const key = item.material_grouping || "(미지정)";
    if (!map.has(key)) {
      map.set(key, {
        material_grouping: key,
        items: [],
      });
    }
    map.get(key).items.push(item);
  });

  return Array.from(map.values());
};

const getTreeLayout = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: "LR",
    nodesep: 80,
    ranksep: 150,
    marginx: 20,
    marginy: 20,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 250, height: 120 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 125,
        y: nodeWithPosition.y - 60,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

const formatEdgeLabel = (quantity, unit, defaultUnit) => {
  const q = quantity ?? "";
  const u = unit || defaultUnit;
  return u ? `${q} ${u}` : String(q);
};

// 여러 Material_grouping 그룹에 대한 BOM 트리 생성 (수직 배치)
const getMultipleItemsLayout = (items, handleOpenEditModal) => {
  const allNodes = [];
  const allEdges = [];
  let yOffset = 0;
  const treeSpacing = 300;

  // 선택된 item 들을 Material_grouping 으로 그룹화
  const groups = groupItemsByMaterialGrouping(items);

  groups.forEach((group, groupIndex) => {
    const treeNodes = [];
    const treeEdges = [];
    let nodeIdCounter = 0;

    // 이 그룹에서 대표 완제품 하나 (완제품 없으면 첫 아이템)
    const representativeItem =
      group.items.find((it) => it.item_type === 1) || group.items[0];

    const productNodeId = `product_group_${groupIndex}`;
    treeNodes.push({
      id: productNodeId,
      type: "productNode",
      data: {
        label: group.material_grouping, // 사내 품명
        item_id: representativeItem.item_id,
        item_code: representativeItem.item_code,
        material_grouping: group.material_grouping,
        safety_stock: representativeItem.safety_stock,
        bomCount: group.items.reduce(
          (sum, it) => sum + (it.subRows?.length || 0),
          0
        ),
        car_type: representativeItem.car_type || "", // 규격으로 쓸 값
        onEdit: () => handleOpenEditModal(representativeItem),
      },
      position: { x: 0, y: 0 },
    });

    // 그룹 안 모든 subRows 를 모아서, 반제품 Material_grouping 기준 1개만
    const subRowMap = new Map();

    group.items.forEach((item) => {
      const rawSubRows = item.main_bom || item.subRows || [];
      rawSubRows.forEach((subItem) => {
        const key =
          subItem.material_grouping ||
          subItem.item_code ||
          subItem.item_name ||
          subItem.material_name;
        if (!key) return;

        // 같은 Material_grouping 이 이미 있으면 스킵 (1:1 유지)
        if (!subRowMap.has(key)) {
          subRowMap.set(key, subItem);
        }
      });
    });

    const uniqueSubRows = Array.from(subRowMap.values());

    uniqueSubRows.forEach((subItem) => {
      const isSemi = subItem.category === "semi" || subItem.item_type === 0;

      const nodeId = `${isSemi ? "semi" : "material"}_${
        subItem.material_id ?? "noid"
      }_${groupIndex}_${nodeIdCounter++}`;

      treeNodes.push({
        id: nodeId,
        type: isSemi ? "semiProductNode" : "materialNode",
        data: {
          label:
            subItem.material_grouping ||
            subItem.item_name ||
            subItem.material_name,
          material_id: subItem.material_id,
          quantity: subItem.quantity,
          qty_unit: subItem.qty_unit || subItem.default_unit,
          isSemi,
          car_type: subItem.car_type || "",
        },
        position: { x: 0, y: 0 },
      });

      treeEdges.push({
        id: `edge_${productNodeId}_${nodeId}`,
        source: productNodeId,
        target: nodeId,
        type: "smoothstep",
        animated: false,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
        label: formatEdgeLabel(
          subItem.quantity,
          subItem.qty_unit,
          subItem.default_unit
        ),
        labelStyle: { fill: "#666", fontWeight: 600, fontSize: 12 },
        labelBgStyle: { fill: "#fff", fillOpacity: 0.8 },
      });
    });

    const { nodes: layoutedTreeNodes, edges: layoutedTreeEdges } =
      getTreeLayout(treeNodes, treeEdges);

    const maxY = Math.max(
      ...layoutedTreeNodes.map((n) => n.position.y + 120),
      0
    );
    const treeHeight = maxY + 100;

    const offsetNodes = layoutedTreeNodes.map((node) => ({
      ...node,
      position: {
        x: node.position.x,
        y: node.position.y + yOffset,
      },
    }));

    allNodes.push(...offsetNodes);
    allEdges.push(...layoutedTreeEdges);
    yOffset += treeHeight + treeSpacing;
  });

  return { nodes: allNodes, edges: allEdges };
};

const nodeTypes = {
  productNode: ProductNode,
  semiProductNode: SemiProductNode,
  materialNode: MaterialNode,
};

const BomManagementContent = () => {
  const { group_code, project_code } = useParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [semiProductMode, setSemiProductMode] = useState(false);

  const { setViewport, getViewport, fitView } = useReactFlow();
  const savedViewportRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const shouldFitViewRef = useRef(true);

  const openSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // 데이터 로드
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = semiProductMode ? "getSemiAll" : "getAll";
      const response = await axios.get(
        `${TPC_URL}/api/bom/${endpoint}?group_code=${group_code}&project_code=${project_code}`
      );
      setItems(response.data || []);
    } catch (error) {
      console.error("Error loading BOM data:", error);
      openSnackbar(
        `데이터 로드 오류: ${error.response?.data?.message || error.message}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code, semiProductMode]);

  useEffect(() => {
    loadData();
  }, [loadData, updateStatus]);

  // 최초 로드 시, 첫 Material_grouping 그룹 전체 선택
  useEffect(() => {
    if (!items || items.length === 0) return;
    if (selectedItemIds.length > 0) return;

    const first = items[0];
    const sameGroupIds = items
      .filter((it) => it.material_grouping === first.material_grouping)
      .map((it) => it.item_id);

    setSelectedItemIds(sameGroupIds);
  }, [items, selectedItemIds.length]);

  // 모드/그룹 변경 시 검색어 초기화
  useEffect(() => {
    setSearchKeyword("");
  }, [group_code, project_code, semiProductMode]);

  const handleModeToggle = (event) => {
    const newMode = event.target.checked;
    setSemiProductMode(newMode);
    setSelectedItemIds([]);
    shouldFitViewRef.current = true;
    openSnackbar(
      `${newMode ? "반제품" : "완제품"} 모드로 전환되었습니다.`,
      "info"
    );
  };

  const handleOpenEditModal = useCallback(
    (item) => {
      savedViewportRef.current = getViewport();
      setSelectedItem({
        parent_item_id: item.item_id,
        parent_item_name: item.item_name,
        parent_material_grouping: item.material_grouping || null,
        existingBomItems: item.main_bom || item.subRows || [],
      });
      setIsEditModalOpen(true);
    },
    [getViewport]
  );

  // 선택된 그룹들에 대한 BOM 트리 생성
  useEffect(() => {
    if (items.length === 0 || selectedItemIds.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const selectedItems = items.filter((item) =>
      selectedItemIds.includes(item.item_id)
    );

    if (selectedItems.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const { nodes: layoutedNodes, edges: layoutedEdges } =
      getMultipleItemsLayout(selectedItems, handleOpenEditModal);

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);

    setTimeout(() => {
      if (isInitialLoadRef.current || shouldFitViewRef.current) {
        fitView({ padding: 0.15, duration: 400 });
        isInitialLoadRef.current = false;
        shouldFitViewRef.current = false;
      } else if (savedViewportRef.current) {
        setViewport(savedViewportRef.current, { duration: 0 });
      }
    }, 100);
  }, [
    items,
    selectedItemIds,
    handleOpenEditModal,
    setNodes,
    setEdges,
    fitView,
    setViewport,
  ]);

  const handleSearchChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  // 그룹 클릭 시: 그 그룹에 속한 모든 item_id 토글
  const handleItemToggle = (group) => {
    const groupIds = group.allItemIds;
    setSelectedItemIds((prev) => {
      const allSelected = groupIds.every((id) => prev.includes(id));
      if (allSelected) {
        // 그룹 해제
        const next = prev.filter((id) => !groupIds.includes(id));
        return next.length > 0 ? next : prev; // 최소 1개 유지
      } else {
        // 그룹 선택
        const merged = new Set([...prev, ...groupIds]);
        return Array.from(merged);
      }
    });
    shouldFitViewRef.current = true;
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
  };

  const handleSaveChanges = (result) => {
    if (result.success) {
      openSnackbar(result.message || "BOM이 업데이트되었습니다.", "success");
      shouldFitViewRef.current = false;
      setUpdateStatus((prev) => !prev);
    } else {
      openSnackbar(result.message || "업데이트에 실패했습니다.", "error");
    }
  };

  // Material_grouping 그룹 + 검색 필터
  const materialGroups = groupItemsByMaterialGrouping(items);

  const filteredGroups = materialGroups
    .map((group) => {
      const keyword = searchKeyword.toLowerCase();
      const mg = String(group.material_grouping).toLowerCase();

      const matchedItems = group.items.filter((item) => {
        const itemName = String(item.item_name).toLowerCase();
        const materialGrouping = String(item.material_grouping).toLowerCase();
        return (
          itemName.includes(keyword) ||
          materialGrouping.includes(keyword) ||
          mg.includes(keyword)
        );
      });

      if (matchedItems.length === 0) return null;

      const representativeItem =
        matchedItems.find((it) => it.item_type === 1) || matchedItems[0];

      return {
        material_grouping: group.material_grouping,
        representativeItem,
        totalBomCount: group.items.reduce(
          (sum, it) => sum + (it.subRows?.length || 0),
          0
        ),
        allItemIds: group.items.map((it) => it.item_id),
      };
    })
    .filter(Boolean);
  return (
    <PageContainer>
      <HeaderContainer>
        <Title variant="h4" sx={{ display: "flex", alignItems: "center" }}>
          {semiProductMode ? "반제품" : "완제품"} BOM 관리 &nbsp;
          <Tooltip title="도움말" sx={{ cursor: "pointer" }}>
            <HelpOutlineIcon />
          </Tooltip>
        </Title>

        <SearchContainer>
          <TextField
            fullWidth
            size="small"
            placeholder="검색 (제품명, Material Grouping)"
            value={searchKeyword}
            onChange={handleSearchChange}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </SearchContainer>

        {/* <FormControlLabel
          control={
            <Switch
              checked={semiProductMode}
              onChange={handleModeToggle}
              color="primary"
            />
          }
          label={semiProductMode ? "반제품" : "완제품"}
        /> */}
      </HeaderContainer>

      <MainContent>
        <ContentContainer>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : nodes.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                {selectedItemIds.length === 0
                  ? "왼쪽에서 항목을 선택하세요."
                  : "BOM 정보가 없습니다."}
              </Typography>
            </Box>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              minZoom={0.1}
              maxZoom={1.5}
              attributionPosition="bottom-left"
            >
              <Background color="#aaa" gap={16} />
              <Controls />
              <MiniMap
                nodeStrokeWidth={3}
                zoomable
                pannable
                nodeColor={(node) => {
                  if (node.type === "productNode") return "#667eea";
                  if (node.type === "semiProductNode") return "#f093fb";
                  return "#a8edea";
                }}
                style={{
                  backgroundColor: "#f8f9fa",
                }}
              />
            </ReactFlow>
          )}
        </ContentContainer>
        <SidePanel elevation={0}>
          <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {semiProductMode ? "반제품" : "완제품"} 목록
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                총 {filteredGroups.length}개
              </Typography>
              <Typography variant="caption" color="primary" fontWeight={600}>
                선택: {selectedItemIds.length}개
              </Typography>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : filteredGroups.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {items.length === 0
                  ? "등록된 항목이 없습니다."
                  : "검색 결과가 없습니다."}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {filteredGroups.map((group, index) => {
                const {
                  material_grouping,
                  representativeItem,
                  totalBomCount,
                  allItemIds,
                } = group;

                const allSelected = allItemIds.every((id) =>
                  selectedItemIds.includes(id)
                );

                return (
                  <React.Fragment key={material_grouping}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={allSelected}
                        onClick={() => handleItemToggle(group)}
                        sx={{
                          borderLeft: allSelected
                            ? "4px solid #1976d2"
                            : "4px solid transparent",
                          "&.Mui-selected": { backgroundColor: "#e3f2fd" },
                          "&.Mui-selected:hover": {
                            backgroundColor: "#bbdefb",
                          },
                        }}
                      >
                        <Checkbox
                          checked={allSelected}
                          tabIndex={-1}
                          disableRipple
                          sx={{ mr: 1 }}
                        />
                        <ListItemText
                          primary={material_grouping}
                          secondary={
                            <>
                              {/* 필요하면 대표 item_code / item_name 정도 추가 */}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                규격: {representativeItem.car_type || "-"}{" "}
                              </Typography>
                              <Chip
                                label={`BOM ${totalBomCount}`}
                                size="small"
                                sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
                              />
                            </>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredGroups.length - 1 && <MuiDivider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </SidePanel>
      </MainContent>

      {isEditModalOpen && (
        <EditModal
          open={isEditModalOpen}
          onClose={handleCloseModal}
          item={selectedItem}
          group_code={group_code}
          project_code={project_code}
          apiUrl={TPC_URL}
          onSave={handleSaveChanges}
          semiProductMode={semiProductMode}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

function BomManagement() {
  return (
    <ReactFlowProvider>
      <BomManagementContent />
    </ReactFlowProvider>
  );
}

export default BomManagement;
